import { climateLegendStyles } from './styles.js';
import { CLIMATE_RAMPS, CLIMATE_NO_DATA_COLOR } from '../climateColors.js';
const DATATYPE_LABELS = {
    rainfall: {
        title: 'Rainfall',
        subtitle: 'Trailing 12 months, total',
        infoHeading: 'Rainfall',
        infoBody: 'Each area is colored by its total rainfall over the trailing 12 months - darker blue means more rain.',
        unit: (v) => `${Math.round(v)} mm`,
    },
    temperature: {
        title: 'Temperature',
        subtitle: 'Trailing 12 months, average',
        infoHeading: 'Temperature',
        infoBody: 'Each area is colored by its average temperature over the trailing 12 months - darker means warmer.',
        unit: (v) => `${v.toFixed(1)}°C`,
    },
};
export class ClimateLegend {
    container;
    legendEl = null;
    visible = false;
    scope = 'neighborhoods';
    datatype = 'rainfall';
    onDatatypeChange;
    min = 0;
    center = 0;
    max = 0;
    source = null;
    constructor(options) {
        this.container = options.container;
        this.onDatatypeChange = options.onDatatypeChange;
        this.injectStyles();
        this.render();
    }
    injectStyles() {
        if (document.getElementById('climate-legend-styles'))
            return;
        const style = document.createElement('style');
        style.id = 'climate-legend-styles';
        style.textContent = climateLegendStyles;
        document.head.appendChild(style);
    }
    render() {
        this.legendEl = document.createElement('div');
        this.legendEl.className = 'climate-legend';
        this.legendEl.innerHTML = this.buildInnerHtml();
        this.attachHandlers();
        this.container.appendChild(this.legendEl);
    }
    buildInnerHtml() {
        const scopeLabel = this.scope === 'neighborhoods' ? 'Neighborhoods' : 'Districts';
        const labels = DATATYPE_LABELS[this.datatype];
        const ramp = CLIMATE_RAMPS[this.datatype];
        const gradientCss = `linear-gradient(to right, ${ramp.join(', ')})`;
        const hasRange = this.max > this.min;
        return `
      <div class="climate-legend-header">
        <div class="climate-metric-toggle">
          <button type="button" class="climate-metric-btn${this.datatype === 'rainfall' ? ' active' : ''}" data-datatype="rainfall">Rainfall</button>
          <button type="button" class="climate-metric-btn${this.datatype === 'temperature' ? ' active' : ''}" data-datatype="temperature">Temperature</button>
        </div>
        <button class="climate-info-btn" title="What does this show?">?</button>
      </div>
      <div class="climate-legend-scope-row" style="display:flex; justify-content:space-between; margin-top: 6px;">
        <span class="climate-legend-subtitle" style="margin:0;">${labels.subtitle}</span>
        <span class="climate-legend-scope">${scopeLabel}</span>
      </div>
      <div class="climate-gradient" style="background: ${gradientCss};"></div>
      <div class="climate-gradient-labels">
        <span>${hasRange ? labels.unit(this.min) : '—'}</span>
        <span title="Island average">${hasRange ? labels.unit(this.center) : ''}</span>
        <span>${hasRange ? labels.unit(this.max) : '—'}</span>
      </div>
      <div class="climate-no-data-row">
        <div class="climate-no-data-swatch" style="background: ${CLIMATE_NO_DATA_COLOR};"></div>
        <span>No data yet</span>
      </div>
      ${this.source ? `
        <div class="climate-attribution">
          Data: <a href="${this.source.url}" target="_blank" rel="noopener noreferrer">${this.source.name}</a>
        </div>
      ` : ''}
    `;
    }
    attachHandlers() {
        this.legendEl?.querySelector('.climate-info-btn')?.addEventListener('click', () => {
            this.showInfoPopup();
        });
        this.legendEl?.querySelectorAll('.climate-metric-btn').forEach((el) => {
            el.addEventListener('click', () => {
                const datatype = el.dataset.datatype;
                if (datatype === this.datatype)
                    return;
                this.datatype = datatype;
                this.refresh();
                this.onDatatypeChange?.(datatype);
            });
        });
    }
    refresh() {
        if (this.legendEl) {
            this.legendEl.innerHTML = this.buildInnerHtml();
            this.attachHandlers();
        }
    }
    showInfoPopup() {
        const labels = DATATYPE_LABELS[this.datatype];
        const overlay = document.createElement('div');
        overlay.className = 'climate-info-overlay';
        const popup = document.createElement('div');
        popup.className = 'climate-info-popup';
        popup.innerHTML = `
      <button class="close-btn">&times;</button>
      <h3>${labels.infoHeading}</h3>
      <p>${labels.infoBody}</p>
      <p>The middle of the scale is the island average, not the midpoint of the lowest and highest areas - Maui's climate varies a lot over short distances (windward vs. leeward), so anchoring to the average keeps the colors spread out instead of a few very wet or hot spots crushing everything else into one shade.</p>
      ${this.source ? `<p>Source: <a href="${this.source.url}" target="_blank" rel="noopener noreferrer">${this.source.name}</a></p>` : ''}
    `;
        const close = () => {
            overlay.remove();
            popup.remove();
        };
        overlay.addEventListener('click', close);
        popup.querySelector('.close-btn')?.addEventListener('click', close);
        document.body.appendChild(overlay);
        document.body.appendChild(popup);
    }
    /** Sets which layer's climate range the legend is currently describing. */
    setScope(scope) {
        this.scope = scope;
        this.refresh();
    }
    /** Sets which datatype the toggle shows as selected, e.g. to stay in sync if changed from outside. */
    setDatatype(datatype) {
        this.datatype = datatype;
        this.refresh();
    }
    getDatatype() {
        return this.datatype;
    }
    setRange(min, center, max) {
        this.min = min;
        this.center = center;
        this.max = max;
        this.refresh();
    }
    setSource(source) {
        this.source = source;
        this.refresh();
    }
    show() {
        this.visible = true;
        this.legendEl?.classList.add('visible');
    }
    hide() {
        this.visible = false;
        this.legendEl?.classList.remove('visible');
    }
    toggle(show) {
        if (show === undefined)
            show = !this.visible;
        if (show)
            this.show();
        else
            this.hide();
    }
    isVisible() {
        return this.visible;
    }
    destroy() {
        this.legendEl?.remove();
        document.getElementById('climate-legend-styles')?.remove();
    }
}
//# sourceMappingURL=ClimateLegend.js.map